import React, { FC, useEffect, useMemo } from 'react';
import { StyledPaper } from '../theme';
import {
  Typography,
  Box,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Pagination,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import moment from 'moment';
import { GetExternalCategoryCommentsForOwnerQuery, GetExternalCommentsForOwnerQuery } from '../__generated__/graphql';
import { Unpacked } from '../graphQLTypes/types';
import { useQuery } from '@apollo/client';
import { formatDateOnly } from '../Formatters';
import { Link as RouterLink } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import { GET_EXTERNAL_CATEGORY_COMMENTS_FOR_OWNER, GET_EXTERNAL_COMMENTS_FOR_OWNER } from './home.graphql';
import { useUserContext } from '../UserContext';
import { CommentItem, firstLine, Thread } from '../comments/CommentThread';

export const Notifications: FC = () => {
  return (
    <StyledPaper>
      <Typography variant="h5" component="h1" gutterBottom>
        Varslinger
      </Typography>
      <Typography variant="body1" component="p">
        Her kommer varslinger om tilbakemeldinger på dine anvisninger og fagområder.
      </Typography>
      <Overview />
    </StyledPaper>
  );
};

type GuideComment = NonNullable<Unpacked<NonNullable<GetExternalCommentsForOwnerQuery['comments']>['nodes']>>;
type CategoryComment = NonNullable<Unpacked<NonNullable<GetExternalCategoryCommentsForOwnerQuery['comments']>['nodes']>>;

interface NotificationRow {
  comment: Thread;
  /** The guide number or the category title. */
  label: string;
  targetLabel: string;
  linkTo: string;
  linkText: string;
}

const Overview: FC = () => {
  const { userId, lastReadNotifications, updateLastReadNotifications, receiveEmailNotifications, toggleEmailNotifications } = useUserContext();
  const guideComments = useQuery(GET_EXTERNAL_COMMENTS_FOR_OWNER, { variables: { ownerId: userId } });
  const categoryComments = useQuery(GET_EXTERNAL_CATEGORY_COMMENTS_FOR_OWNER, { variables: { ownerId: userId } });
  const loaded = !!guideComments.data && !!categoryComments.data;

  useEffect(() => {
    if (loaded) {
      setTimeout(() => updateLastReadNotifications(new Date()), 4000);
    }
  }, [loaded, updateLastReadNotifications]);

  const rows = useMemo<NotificationRow[]>(
    () =>
      [
        ...(guideComments.data?.comments?.nodes ?? [])
          .filter((c): c is GuideComment => !!c)
          .map<NotificationRow>((c) => ({
            comment: c,
            label: c.guide?.docName ?? '',
            targetLabel: 'Anvisning:',
            linkTo: `/guide/${c.guide?.id}#comment-${c.id}`,
            linkText: `${c.guide?.docName ?? ''} ${c.guide?.docTitle ?? ''}`,
          })),
        ...(categoryComments.data?.comments?.nodes ?? [])
          .filter((c): c is CategoryComment => !!c)
          .map<NotificationRow>((c) => ({
            comment: c,
            label: c.category?.title ?? '',
            targetLabel: 'Fagområde:',
            linkTo: `/category/${c.category?.id}#comment-${c.id}`,
            linkText: c.category?.title ?? '',
          })),
      ].sort((a, b) => moment(b.comment.createdAt).valueOf() - moment(a.comment.createdAt).valueOf()),
    [guideComments.data, categoryComments.data],
  );

  if (!loaded) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <FormGroup row style={{ justifyContent: 'flex-end' }}>
        <FormControlLabel
          control={<Checkbox checked={receiveEmailNotifications} onChange={toggleEmailNotifications} />}
          label="Få varsler på e-post"
        />
      </FormGroup>
      <Stack mt={2} spacing={2}>
        <Typography variant="body1" component="h2" fontWeight="500">
          Åpne tilbakemeldinger
        </Typography>
        {rows.length === 0 && <Typography sx={{ mt: 2 }}>Ingen åpne tilbakemeldinger</Typography>}
        <NotificationList rows={rows} lastRead={lastReadNotifications} />
      </Stack>
    </div>
  );
};

const PAGE_SIZE = 20;

/** Matches the overview's label column, so both lists truncate the same way. */
const LABEL_COLUMN_WIDTH = 180;

const NotificationList: FC<{ rows: NotificationRow[]; lastRead: Date }> = ({ rows, lastRead }) => {
  const [page, setPage] = React.useState(1);

  const pageCount = Math.ceil(rows.length / PAGE_SIZE);
  const paginatedRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  React.useEffect(() => {
    setPage(1);
  }, [rows]);

  return (
    <Stack>
      {paginatedRows.map((row) => {
        const c = row.comment;
        const isNew = moment(c.createdAt).isAfter(moment(lastRead));
        const fontWeight = isNew ? 500 : undefined;
        return (
          <Accordion key={c.id}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              {/* Fixed label column so the date lines up across rows; see Overview.tsx. */}
              <Box sx={{ display: 'grid', alignItems: 'center', gridTemplateColumns: `1fr ${LABEL_COLUMN_WIDTH}px auto`, gap: 2, width: '100%' }}>
                <Typography fontWeight={fontWeight} sx={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {firstLine(c.text)}
                </Typography>
                <Typography
                  fontWeight={fontWeight}
                  color="textSecondary"
                  title={row.label}
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {row.label}
                </Typography>
                <Typography fontWeight={fontWeight} color="textSecondary" whiteSpace="nowrap">
                  {formatDateOnly(c.createdAt)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack>
                <CommentItem comment={c} selected={false} />
                <Box display="grid" gridTemplateColumns="max-content auto" columnGap={2} mt={2}>
                  <Typography color="textSecondary">{row.targetLabel}</Typography>
                  <Link component={RouterLink} to={row.linkTo}>
                    {row.linkText}
                  </Link>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
      {pageCount > 1 && (
        <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} sx={{ mt: 2, display: 'flex', justifyContent: 'center' }} />
      )}
    </Stack>
  );
};
